import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  FileText,
  Download,
  Loader2,
  Receipt
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const translations = {
  es: {
    title: 'Mis Facturas',
    subtitle: 'Historial de todas tus facturas',
    back: 'Volver',
    noInvoices: 'No tienes facturas todavía',
    noInvoicesDesc: 'Cuando realices una recarga o pedido, aparecerán aquí',
    invoiceNumber: 'Nº Factura',
    date: 'Fecha',
    amount: 'Importe',
    vat: 'IVA',
    total: 'Total',
    status: 'Estado',
    download: 'Descargar',
    loading: 'Cargando facturas...',
    paid: 'Pagada',
    pending: 'Pendiente',
  },
  en: {
    title: 'My Invoices',
    subtitle: 'History of all your invoices',
    back: 'Back',
    noInvoices: 'No invoices yet',
    noInvoicesDesc: 'When you make a recharge or order, they will appear here',
    invoiceNumber: 'Invoice #',
    date: 'Date',
    amount: 'Amount',
    vat: 'VAT',
    total: 'Total',
    status: 'Status',
    download: 'Download',
    loading: 'Loading invoices...',
    paid: 'Paid',
    pending: 'Pending',
  },
};

const MyInvoicesPage = () => {
  const { token } = useAuth();
  const { language, tx } = useAdminTranslation();
  const t = translations[language] || translations.es;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/my-invoices`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          setInvoices(data.invoices || []);
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvoices();
  }, [token]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(tx('es-ES', 'en-US'), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDownload = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="h-6 w-6 text-primary" />
              {t.title}
            </h1>
            <p className="text-muted-foreground text-sm">{t.subtitle}</p>
          </div>
        </div>

        {/* Invoices List */}
        <Card data-testid="invoices-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              {t.title}
            </CardTitle>
            <CardDescription>
              {invoices.length > 0 
                ? `${invoices.length} ${tx('factura(s) encontrada(s)', 'invoice(s) found')}`
                : t.noInvoicesDesc
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-12" data-testid="no-invoices-message">
                <Receipt className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">{t.noInvoices}</h3>
                <p className="text-muted-foreground text-sm">{t.noInvoicesDesc}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div 
                    key={invoice.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:border-primary/30 transition-colors gap-4"
                    data-testid={`invoice-item-${invoice.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{invoice.invoice_number}</span>
                        <Badge 
                          className={invoice.status === 'Pagada' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                        >
                          {invoice.status === 'Pagada' ? t.paid : t.pending}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(invoice.invoice_date)}
                      </p>
                      {invoice.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {invoice.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {t.amount}: {invoice.base_amount?.toFixed(2)} €
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t.vat} ({invoice.iva}%): {((invoice.base_amount || 0) * (invoice.iva || 0) / 100).toFixed(2)} €
                        </p>
                        <p className="font-bold text-primary">
                          {t.total}: {invoice.total?.toFixed(2)} €
                        </p>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(invoice.id)}
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {t.download}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MyInvoicesPage;
